import { AfterViewChecked, Component, ElementRef, OnInit, Renderer2, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CustomerFeConfig } from 'src/app/modules/core/interfaces/environment.interface';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import { LoaderState } from './modules/core/enums/loader-state';
import { LogoutAction } from './modules/core/enums/logout-action.enum';
import { NotifyOrConfirmType } from './modules/core/enums/notify-or-confirm-type.enum';
import { SessionTimeoutType } from './modules/core/enums/session-timeout-type.enum';
import { AuthService } from './modules/core/services/auth.service';
import { InitService } from './modules/core/services/init.service';
import { LocaleService } from './modules/core/services/locale.service';
import { LoaderService } from './modules/core/services/loader.service';
import { MenuService } from './modules/core/services/menu.service';
import { NotifyOrConfirmService } from './modules/core/services/notify-or-confirm.service';
import { SessionService } from './modules/core/services/session.service';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { RouterDataService } from './modules/core/services/router-data.service';
import { UserService } from './modules/core/services/user.service';
import { ScrollerService } from './modules/core/services/scroller.service';
import { WindowRefService } from './modules/core/services/window.service';
import { LanguageService } from './modules/core/services/language.service';

@Component({
	selector: 'cfe-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
	@ViewChild('content', { static: true }) contentElement: ElementRef;
	@ViewChild('spinner', { static: true }) spinnerElement: ElementRef;
	@ViewChild('cookieLaw', { static: true }) cookieLawEl: any;
	@ViewChild('scrollContent', { static: true }) scrollContent: ElementRef;

	// Internal or external page
	public internal = false;

	public opened: boolean;
	public hideContent: BehaviorSubject<LoaderState>;
	public externalLoader: BehaviorSubject<LoaderState>;
	public cookieText: string;
	public currentUser: boolean;
	public config: CustomerFeConfig;
	private termsOfUseAnchor: string;
	private useOfCookiesAnchor: string;
	private cookiePolicyAnchor: string;
	private translateSubscription: Subscription;
	private subscriptions: Subscription[] = [];

	constructor(
		private authenticationService: AuthService,
		private translateService: TranslateService,
		private configService: ConfigService,
		private cookieService: CookieService,
		protected localeService: LocaleService,
		private sessionService: SessionService,
		private languageService: LanguageService,
		private loaderService: LoaderService,
		private initService: InitService,
		private menuService: MenuService,
		private notifyOrConfirmService: NotifyOrConfirmService,
		private routerDataService: RouterDataService,
		private renderer: Renderer2,
		private router: Router,
		private userService: UserService,
		private scrollerService: ScrollerService,
		private windowRef: WindowRefService
	) {}

	ngOnInit() {
		// Get newest config
		this.configService.configObservable.subscribe((config) => {
			this.config = config;
		});

		// Scroll to top on page switch
		this.subscribeRouterChange();

		// Internal or external page to switch framework
		this.subscriptions.push(
			this.routerDataService.dataObservable.subscribe((data) => {
				this.internal = !!data.internal;
			})
		);

		this.hideContent = this.loaderService.loader.getStateObservable();
		this.externalLoader = this.loaderService.externalLoader.getStateObservable();

		this.subscriptions.push(
			this.sessionService.currentUserObservable.subscribe((user) => {
				this.currentUser = !!user;
			})
		);

		// Update user on page (re)load
		const currentUser = this.sessionService.currentUser;

		if (currentUser) {
			this.userService.getUserWithCredit(currentUser.uid).subscribe((userWithCreditData) => {
				this.sessionService.currentUser = userWithCreditData.user;
			});
		}

		this.subscriptions.push(
			this.menuService.menuStateObservable.subscribe((menuState: boolean) => {
				this.opened = menuState;
			})
		);

		this.languageService.language$.subscribe((languageCode: string) => {
			if (languageCode) {
				this.setCookieLinks();
				// check for automatic Logout information to show
				// set expiredLogout to top , to avoid showing securityLogout at this point
				if (this.cookieService.check('expiredLogout')) {
					this.translateSubscription = this.translateService
						.get(['logout.expiredLogout.title', 'logout.expiredLogout.message', 'notificationOrConfirm.confirm'])
						.subscribe((text: Object) => {
							this.showModal(text, LogoutAction.EXPIRED);
						});
				} else if (this.cookieService.check('securityLogout')) {
					this.translateSubscription = this.translateService
						.get(['logout.securityLogout.title', 'logout.securityLogout.message', 'notificationOrConfirm.confirm'])
						.subscribe((text: Object) => {
							this.showModal(text, LogoutAction.SECURITY);
						});
				} else if (this.cookieService.check('timeoutLogout')) {
					this.translateSubscription = this.translateService
						.get(['logout.timeoutLogout.title', 'logout.timeoutLogout.message', 'notificationOrConfirm.confirm'])
						.subscribe((text: Object) => {
							this.showModal(text, LogoutAction.TIMEOUT);
						});
				} else if (this.cookieService.check('redirectToLogin')) {
					this.cookieService.delete('redirectToLogin', '/');
					this.windowRef.nativeWindow.open(this.config.loginUri + 'customerFe', '_self');
				}
			}

			this.translateService
				.get('appComponent.text.aboutCookies', {
					termsOfUseAnchor: this.termsOfUseAnchor,
					useOfCookiesAnchor: this.useOfCookiesAnchor,
					cookiePolicyAnchor: this.cookiePolicyAnchor,
					anchorClosingTag: '</a>'
				})
				.subscribe((translatedCookieText) => {
					this.cookieText = translatedCookieText;
				});
		});
	}

	ngAfterViewInit(): void {
		// Initialize ScrollerService
		this.scrollerService.init(this.scrollContent.nativeElement.parentElement);
	}

	ngAfterViewChecked() {
		this.adjustSpinnerHeight();
	}

	ngOnDestroy() {
		this.subscriptions.forEach((subscription: Subscription) => {
			subscription.unsubscribe();
		});
	}

	public adjustSpinnerHeight() {
		if (this.contentElement && this.spinnerElement) {
			const contentHeight = this.contentElement.nativeElement.offsetHeight;
			this.renderer.setStyle(this.spinnerElement.nativeElement, 'height', contentHeight + 'px');
		}
	}

	public showModal(text: Object, type: LogoutAction) {
		let title;
		let message;
		const confirm = text['notificationOrConfirm.confirm'];

		// check which information should be shown
		switch (type) {
			case LogoutAction.EXPIRED:
				title = text['logout.expiredLogout.title'];
				message = text['logout.expiredLogout.message'];
				break;

			case LogoutAction.SECURITY:
				title = text['logout.securityLogout.title'];
				message = text['logout.securityLogout.message'];
				break;

			case LogoutAction.TIMEOUT:
				title = text['logout.timeoutLogout.title'];
				message = text['logout.timeoutLogout.message'];
				break;
		}

		// delete all information cookies
		this.cookieService.delete('securityLogout', '/');
		this.cookieService.delete('timeoutLogout', '/');
		this.cookieService.delete('expiredLogout', '/');

		// show translated Notification Modal
		this.notifyOrConfirmService.showNotification(title, message, NotifyOrConfirmType.NOTIFICATION, confirm);
	}

	/**
	 * Result from SessionTimeOut Component
	 * Here we can handle each result string
	 *
	 * @param event
	 */
	public resultModal(event) {
		if (event === SessionTimeoutType.IGNORED) {
			this.cookieService.set('timeoutLogout', 'true', 0, '/');
			this.authenticationService.logout().subscribe();
			return;
		}

		if (event === SessionTimeoutType.LOGOUT) {
			this.authenticationService.logout().subscribe();
			return;
		}

		if (event === SessionTimeoutType.KEEP) {
			return;
		}
	}

	public closeLoader() {
		this.loaderService.loader.stopLoading();
	}

	public closeSidebar() {
		this.menuService.menuState = false;
	}

	public dismissCookieBar(): void {
		this.cookieLawEl.dismiss();
	}

	/**
	 * Set Cookie links for each language
	 * currently set to german, because only this one is available
	 *
	 * @param language
	 */
	public setCookieLinks() {
		// todo change in other Links if available
		this.termsOfUseAnchor = '<a href="' + this.config.vanityUrls.termsAndConditions + '">';
		this.useOfCookiesAnchor = '<a href="' + this.config.vanityUrls.privacyLink + '#cookies">';
		this.cookiePolicyAnchor = '<a href="' + this.config.vanityUrls.privacyLink + '">';
	}

	private subscribeRouterChange() {
		this.subscriptions.push(
			this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
				// For internal: Reload internal page on menu click
				if (this.config.reloadPageOnMenuClick && this.internal) {
					// Reload internal page on menu click
					// See https://github.com/angular/angular/issues/13831#issuecomment-319634921
					this.router.routeReuseStrategy.shouldReuseRoute = () => {
						return false; // Override the route reuse strategy
					};

					// Trick the Router into believing it's last link wasn't previously loaded on menu click
					this.router.navigated = false;
				}

				this.scrollerService.scrollTop();
			})
		);
	}
}
